<script setup>
import { Inertia } from "@inertiajs/inertia";
import { ref, onMounted, watch } from "vue";
import { useForm } from "@inertiajs/inertia-vue3";
import WidgetLayout from "@/Layouts/WidgetLayout.vue";
import SlotlistItem from "@/Components/Widget/SlotlistItem.vue";
import { DatePicker, Calendar } from "v-calendar";

const props = defineProps({
  office: Object,
  type_id: Number,
  practitioner_id: Number,
  insurance: Number,
  video: Boolean,
  pain: Boolean,
  mo: String,
  di: String,
  mi: String,
  do: String,
  fr: String,
  month: String,
  date: String,
  widgetkey: String,
  widgeteid: String,
  errors: Object,
  free_slots: Object,
});

const form = useForm({
  type_id: props.type_id,
  insurance: props.insurance,
  video: props.video,
  pain: props.pain,
  slotData: null,
});
const backUrl = ref("/");
const dateAttributes = ref([]);
const disabledDates = ref([]);
const date = ref(props.date);
const start = ref(null);
const end = ref(null);
const slots = ref({});
const selectedSlotIndex = ref(null);
const slotCount = ref(0);

let pymChild = null;

// watch errors
watch(
  () => props.errors,
  (errors) => {
    if (errors.slotData) {
      // selected slot is not available anymore
      pickDate({ date: date.value });
    }
  }
);

onMounted(() => {
  //console.log("props", props);

  if (typeof pym !== "undefined") {
    pymChild = new pym.Child({ id: props.widgeteid });
  }

  // diable vacations on calendar
  props.office.vacations.forEach((vacation) => {
    let start = new Date(vacation.start);
    let end = new Date(vacation.end);

    disabledDates.value.push({
      start: start,
      end: end,
    });

    dateAttributes.value[0] = {
      key: "vacation",
      dates: start,
      popover: {
        label: "Praxisurlaub",
      },
    };
  });

  handleFreeSlotData(props.free_slots);
});

function pickDate(e) {
  //console.log('pickDate', e);
  date.value = e.date;

  dateAttributes.value[0] = {
    key: "picked",
    highlight: true,
    dates: date.value,
  };

  getFreeSlots();
}

function getFreeSlots() {
  // deselect slot
  selectedSlotIndex.value = null;
  form.slotData = null;

  axios
    .post(route("widget.iframe.get_free_slots", { key: props.widgetkey }), {
      type_id: props.type_id,
      practitioner_id: props.practitioner_id,
      insurance: props.insurance,
      video: props.video,
      pain: props.pain,
      mo: props.mo,
      di: props.di,
      mi: props.mi,
      do: props.do,
      fr: props.fr,
      start: date.value.toISOString().split("T")[0],
    })
    .then((resp) => {
      //console.log("getFreeSlots resp", resp.data);
      handleFreeSlotData(resp.data);
    })
    .catch((err) => {
      console.error("getFreeSlots err", err);
    });
}

function handleFreeSlotData(slotData) {
  start.value = slotData.timespan[0];
  end.value = slotData.timespan[1];
  slots.value = slotData.slots;
  slotCount.value = Object.keys(slots.value).length;
  updateCalendar(slotData);

  if (pymChild) {
    //console.log('sendHeight', pymChild);
    pymChild.sendHeight();
  }
}

function updateCalendar(data) {
  // show selected range
  dateAttributes.value[0] = {
    key: "picked",
    highlight: {
      start: { color: "gray", fillMode: "outline" },
      base: { color: "gray", fillMode: "light" },
      end: { color: "gray", fillMode: "outline" },
    },
    dates: {
      start: new Date(data.timespan[0]),
      end: new Date(data.timespan[1]).setDate(
        new Date(data.timespan[1]).getDate()
      ),
    },
  };
}

function formatDate(inputDate, timezone = true) {
  //console.log("formatDate", inputDate);
  let date = new Date(inputDate);

  if (timezone) {
    let utc = date.getTime() + date.getTimezoneOffset() * 60000; // deal with timezone in milliseconds
    date = new Date(utc);
  }

  let day = date.getDate().toString().padStart(2, "0"),
    month = (date.getMonth() + 1).toString().padStart(2, "0"),
    year = date.getFullYear();

  return `${day}.${month}.${year}`;
}

function selectSlot({ slotData, index }) {
  //console.log("selectSlot", slotData, index);
  form.slotData = slotData;
  selectedSlotIndex.value = index;
}

function submitForm() {
  let url = route("widget.iframe.step3", { key: props.widgetkey });
  Inertia.visit(url, {
    method: "get",
    data: {
      eid: props.widgeteid,
      type_id: form.type_id,
      insurance: form.insurance,
      video: form.video,
      pain: form.pain,
      slotData: form.slotData,
    },
    preserveState: true,
  });
}

function backClicked() {
  let url = route("widget.iframe.step1", {
    key: props.widgetkey,
    type_id: props.type_id,
    practitioner_id: props.practitioner_id,
    insurance: props.insurance,
    video: props.video,
    pain: props.pain,
    mo: props.mo,
    di: props.di,
    mi: props.mi,
    do: props.do,
    fr: props.fr,
    month: props.month,
  });
  backUrl.value = url;
  /* backUrl.value = window.history.back()
        + '?type_id=' + props.type_id + '&insurance=' + props.insurance + '&video=' + props.video + '&pain=' + props.pain
        + '&mo=' + props.mo + '&di=' + props.di + '&mi=' + props.mi + '&do=' + props.do + '&fr=' + props.fr; */
}
</script>

<template>
  <WidgetLayout :office="props.office">
    <div class="mb-8">
      <h2 class="text-3xl font-semibold mb-2">Verfügbare Termine</h2>
      <div class="text-sm" v-if="slotCount">
        {{ slotCount }} freie Termine vom {{ formatDate(start) }} bis
        {{ formatDate(end) }}
      </div>
      <div v-else>&nbsp;</div>
    </div>

    <div class="lg:flex">
      <div class="lg:w-1/2 lg:border-r">
        <!-- left -->
        <div class="h-[32rem] overflow-auto">
          <div v-for="(slot, index) in slots" :key="index" class="w-full mb-4">
            <SlotlistItem
              :slotData="slot"
              :slotIndex="parseInt(index)"
              :selectedSlotIndex="selectedSlotIndex"
              @selected="selectSlot"
            />
          </div>
          <div
            class="sticky bottom-0 h-10 w-full bg-gradient-to-t from-white"
          ></div>
        </div>
      </div>

      <div class="lg:w-1/2 lg:pl-16">
        <!-- right -->
        <div class="mt-8 pb-8">
          <Calendar
            data-helptext=""
            class="w-full border-none pl-8"
            timezone="UTC"
            show-iso-weeknumbers="left-outside"
            @dayclick="pickDate"
            :min-date="new Date()"
            :attributes="dateAttributes"
            :disabled-dates="disabledDates"
            :from-date="new Date(date)"
            :locale="{
              id: 'de',
              masks: { weekdays: 'WW' },
            }"
          />

          <!--<div class="flex">
                        <span
                            class="inline-block w-4 h-4 mr-2 mt-1 rounded-full shadow"
                            :style="`background-color:#cbd5e0;`"
                        ></span> Praxisurlaub
                    </div>-->

          <div class="mt-2 text-red-600 text-sm" v-if="errors.slotData">
            {{ errors.slotData }}
          </div>
        </div>
      </div>
    </div>

    <!--<div class="mt-8">
            <a @click="getFreeSlots()">Test</a>
        </div>-->

    <div class="mt-12 py-6 flex justify-between">
      <a
        class="px-6 md:px-20 py-3 text-xl text-gray-500 bg-transparent hover:bg-gray-200 border border-gray-500 rounded-lg cursor-pointer"
        @click="backClicked"
        :href="backUrl"
        >Zurück</a
      >

      <button
        class="px-6 md:px-20 py-3 text-xl font-semibold rounded-lg text-white bg-primary hover:opacity-70 cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
        @click="submitForm()"
        :disabled="!form.slotData"
      >
        Weiter
      </button>
    </div>
  </WidgetLayout>
</template>

<style>
:root {
  --spitta-primary: 0 110 180;
  --spitta-primary-hex: #006eb4;
  --color-primary: var(--spitta-primary);
}

.bg-primary {
  background-color: #006eb4;
}
.vc-container .vc-highlight {
  background-color: #006eb4;
}

.vc-day .is-disabled {
  pointer-events: none;
}
</style>
